/**
 * 웹의 챌린지 가져오기 = **클라우드(QBase)에 저장**(사용자 결정 2026-10-06). 스탠드얼론은 지금처럼 로컬 저장소(importDoc).
 *
 * 흐름: 파일 검사(백엔드 exchange.js — 형식 판정은 한 곳) → 클라우드 목록과 대조해 계획(planCloudImport, 순수 함수)
 *       → 챌린지마다 **제출**(submitChallenge — 머리 + 문항 한 번에). 가져오기도 공용 LLM 판정을 거친다(사용자 결정 2026-10-06):
 *       통과·판정 생략은 저장, 보완·거절은 저장하지 않고 사유를 돌려준다 — 보완은 사람이 확인해야 하므로 일괄로 받아들이지 않는다
 *       (편집기에서 그 챌린지를 열어 고치거나 보완을 받아들인다). 하루 한도(429)에 걸리면 나머지는 멈춘다.
 *
 * **같은 것을 다시 가져오면 갱신한다**(중복을 만들지 않는다). 클라우드는 번호를 서버가 새로 정하므로(EDU_TUNING → QC000xx)
 * 원래 번호를 속성 JSON 의 `srcId` 로 적어 두고 그것으로 찾는다. 클라우드에서 내보낸 파일(번호가 QC000xx)은 그 번호로 찾는다.
 * 고칠 수 있는 것(내 것·관리자)만 갱신하고, 남의 것과 번호가 같으면 **내 사본을 새로 만든다** — 남의 챌린지를 덮지 않는다
 * (권위는 QBase 의 소유 검사다 — 화면 판정은 길 안내).
 */
import axios from 'axios';
import i18n from '../i18n';
import { listChallenges, submitQbaseChallenge } from './livesqlRepo';
import { LOCAL_API } from './challengeSource';
import { attrForExport, parseChallengeAttr } from '../utils/challengeAttr';
import { qbaseDate } from '../utils/challengeEdit';

const MAX_QUIZZES = 10;

const today8 = (now = new Date()) => {
  const p = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}`;
};

/** 클라우드 목록의 행(QBase camelCase) — 번호·작성자·속성만 본다 */
const rowId = (r) => String(r.qryChallengeId ?? r.QRY_CHALLENGE_ID ?? r.challengeId ?? '');
const rowOwner = (r) => String(r.mgrUsrid ?? r.MGR_USRID ?? '');
const rowAttr = (r) => parseChallengeAttr(r.challengeAttrCn ?? r.CHALLENGE_ATTR_CN);

/**
 * 가져오기 계획 — 파일의 챌린지마다 만들지(create) 갱신할지(update, targetId).
 * @param fileChallenges 교환 형식 챌린지(문항 포함) — exchange.parse 를 지난 것
 * @param cloudRows      클라우드 챌린지 목록
 * @param opts { me: 지금 usrid, isAdmin: LSADMIN 인가 }
 */
export function planCloudImport(fileChallenges, cloudRows, { me, isAdmin = false } = {}) {
  const canWrite = (r) => isAdmin || (!!me && rowOwner(r) === String(me));
  return (fileChallenges || []).map((c) => {
    const id = String(c.challengeId ?? '');
    const srcId = parseChallengeAttr(c.challengeAttrCn).srcId || id;
    // 1) 같은 클라우드 챌린지(클라우드에서 내보낸 파일) 2) 같은 원래 번호(srcId) — 고칠 수 있는 것만
    const sameCloud = (cloudRows || []).find(r => rowId(r) === id && canWrite(r));
    const sameSrc = (cloudRows || []).find(r => rowAttr(r).srcId === srcId && canWrite(r));
    const target = sameCloud || sameSrc || null;
    const othersHave = !target && (cloudRows || []).some(r => rowId(r) === id || rowAttr(r).srcId === srcId);
    return { challenge: c, srcId, action: target ? 'update' : 'create', targetId: target ? rowId(target) : null, copyOfOthers: othersHave };
  });
}

/** 파일 챌린지 → 클라우드 머리 정보(교환 형식 camelCase). 날짜는 14자리, 비면 오늘 ~ 2099-12-31 */
export function cloudHeaderOf(c, srcId, me) {
  const attr = parseChallengeAttr(attrForExport(c, c.quizzes));
  const draft = attr.status === 'draft';
  return {
    challengeNm: String(c.challengeNm || c.challengeId || '').trim(),
    challengeLevelDcd: ['01', '02', '03'].includes(String(c.challengeLevelDcd)) ? String(c.challengeLevelDcd) : '01',
    bgngDt: qbaseDate(c.bgngDt || today8(), 'begin'),
    endDt: qbaseDate(c.endDt || '20991231', 'end'),
    // 작성중은 비공개로(상태를 모르는 쪽에서도 적어도 비공개로 읽히게)
    publicYn: draft ? 'N' : (c.publicYn === 'N' ? 'N' : 'Y'),
    challengeAttrCn: JSON.stringify({ ...attr, v: attr.v ?? 1, srcId }),
    mgrUsrid: me || null,
  };
}

/** 문항을 1..n 으로 다시 매긴다(화면 슬롯이 번호로 그린다) */
const renumbered = (quizzes) => [...(quizzes || [])]
  .sort((a, b) => Number(a.quizNo || 0) - Number(b.quizNo || 0))
  .map((q, i) => ({ ...q, quizNo: i + 1 }));

/**
 * 파일 문서를 클라우드에 넣는다. 챌린지 하나가 실패해도 나머지는 계속한다 — 실패는 이름과 사유로 돌려준다.
 * @returns {{ challengeCount, quizCount, created, updated, failed: [{ name, message }], warnings }}
 */
export async function importDocToCloud(doc, { me, isAdmin = false } = {}) {
  const parsed = (await axios.post(`${LOCAL_API}/exchange/parse`, doc)).data;
  const cloudRows = await listChallenges();
  const plan = planCloudImport(parsed.challenges, cloudRows, { me, isAdmin });
  const out = { challengeCount: 0, quizCount: 0, created: 0, updated: 0, failed: [], warnings: [...(parsed.warnings || [])] };
  for (const step of plan) {
    const c = step.challenge;
    const name = c.challengeNm || c.challengeId;
    const quizzes = renumbered(c.quizzes);
    if (quizzes.length > MAX_QUIZZES) {
      out.failed.push({ name, message: i18n.t('challenge:file.cloudTooMany', { n: quizzes.length, max: MAX_QUIZZES }) });
      continue;
    }
    try {
      // 작성자는 서버가 세션으로 정한다 — 보내지 않는다
      const { mgrUsrid: _owner, ...header } = cloudHeaderOf(c, step.srcId, me);
      // eslint-disable-next-line no-await-in-loop
      const res = await submitQbaseChallenge({ id: step.targetId, header, quizzes });
      if (res.verdict === 'pass' || res.verdict === 'notRequired') {
        if (step.action === 'create' && step.copyOfOthers) out.warnings.push(i18n.t('challenge:file.cloudCopied', { name, id: res.qryChallengeId }));
        out.challengeCount += 1;
        out.quizCount += quizzes.length;
        if (step.action === 'create') out.created += 1; else out.updated += 1;
      } else {
        out.failed.push({ name, verdict: res.verdict, message: i18n.t(`challenge:file.cloudVerdict.${res.verdict}`, { reason: res.reason || '' }) });
      }
    } catch (e) {
      out.failed.push({ name, message: e?.message || String(e) });
      // 하루 한도 — 나머지를 내도 다 같은 답이다. 멈추고 몇 개가 남았는지 말한다
      if (e && e.status === 429) {
        out.stoppedAtLimit = plan.length - plan.indexOf(step) - 1;
        break;
      }
    }
  }
  return out;
}
